/** Shape of pydantic-evals `EvaluationReport` JSON stored in `app_calls.eval_report_json`. */

import { isRecord } from '@/lib/api'
import { ratingLevelName } from '@/features/evaluation-rubrics/rubricTypes'

/** True when `app_calls.eval_report_json` has usable content. */
export function hasNonEmptyEvalReportJson(value: unknown): boolean {
  if (value == null) {
    return false
  }
  if (typeof value === 'string') {
    return value.trim().length > 0
  }
  if (Array.isArray(value)) {
    return value.length > 0
  }
  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).length > 0
  }
  return true
}

type Probability = { label: string; probability: number }

/**
 * Whether the judge saw the agent's system prompt: `used`, `missing` when the
 * rubric asked for it but the call had no single prompt (none stored, or a
 * handoff between duties) and was judged on the transcript alone, or null
 * when the rubric didn't ask.
 */
export type AgentPromptUse = 'used' | 'missing' | null

function agentPromptUse(args: Record<string, unknown> | null): AgentPromptUse {
  if (args?.include_agent_prompt !== true) {
    return null
  }
  return args.agent_prompt_found === true ? 'used' : 'missing'
}

/**
 * One rubric's result, shaped by its type. Helpcare's decision-model judge
 * writes every type into the pydantic-evals report (see `cf_call_eval.py`):
 *
 * - pass/fail: assertion `{title}`, score `{title} p(pass)`
 * - classification: label `{title}` (the pick), scores `{title} confidence`
 *   and `{title} p({label})` per label
 * - multi-label: assertion `{title}: {label}` per label, its reason
 *   "p = 0.97 (applies at 0.45)"
 * - rating: score `{title}` (the expected level, 1-based), label
 *   `{title} level` (the nearest level), scores `{title} confidence` and `{title} p({n})`
 *
 * Each entry's `source.arguments` carries the rubric's `title`,
 * `rubric_type` and, for a rating, its level names (`levels`), which is how
 * entries are grouped back into rubrics. A classification's or multi-label's
 * labels are read from its own entry names, so the report doesn't repeat the
 * option list on every entry. Reports from before rubric types have no
 * `rubric_type`, so they read as pass/fail; a type this parser doesn't know
 * reads as `unsupported` rather than as a failing pass/fail.
 *
 * A rubric set to include the agent's system prompt also carries
 * `include_agent_prompt` and `agent_prompt_found`; `agentPrompt` reads them.
 */
export type RubricResult = {
  key: string
  title: string
  reason: string | null
  agentPrompt: AgentPromptUse
  /** Hoku's mark for Careforce staff: the org's customers don't see this. */
  careforceOnly: boolean
} & (
  | { type: 'pass_fail'; passed: boolean; probability: number | null }
  | {
      type: 'classification'
      choice: string | null
      probabilities: Probability[]
    }
  | {
      type: 'multi_label'
      labels: {
        label: string
        applies: boolean
        probability: number | null
        /** Relay's "p = … (applies at 0.45)", which carries the threshold. */
        reason: string | null
      }[]
    }
  | {
      type: 'rating'
      /** Expected level on a 1-based scale, e.g. 2.4 of 3. */
      score: number | null
      levels: number
      /** The nearest level's name, as the judge reported it. */
      levelName: string | null
      probabilities: Probability[]
    }
  | {
      /** A rubric type newer than this parser: shown, but not interpreted. */
      type: 'unsupported'
      rubricType: string
    }
)

/** A rubric whose judge raised, so the report has no result for it. */
export type UngradedRubric = {
  key: string
  title: string
  /** The rubric's configured type, as the failed evaluator recorded it. */
  rubricType: string
  error: string | null
  careforceOnly: boolean
}

export type ParsedRubricReport = {
  rubrics: RubricResult[]
  /** Rubrics relay couldn't grade; a partial report still stores the rest. */
  ungraded: UngradedRubric[]
}

type Entry = {
  /** The report's dict key, unique per kind (pydantic-evals suffixes clashes). */
  key: string
  name: string
  value: unknown
  reason: string | null
  args: Record<string, unknown> | null
}

function entries(raw: unknown): Entry[] {
  if (!isRecord(raw)) {
    return []
  }
  const out: Entry[] = []
  for (const [key, item] of Object.entries(raw)) {
    if (!isRecord(item)) {
      continue
    }
    const source = isRecord(item.source) ? item.source : null
    const args = source && isRecord(source.arguments) ? source.arguments : null
    out.push({
      key,
      name: typeof item.name === 'string' && item.name.trim() ? item.name : key,
      value: item.value,
      reason:
        typeof item.reason === 'string'
          ? item.reason
          : item.reason == null
            ? null
            : String(item.reason),
      args,
    })
  }
  return out
}

/** A rating's level names, lowest first, from its `levels` argument. */
function levelNames(args: Record<string, unknown> | null): string[] {
  const names = args?.levels
  if (!Array.isArray(names)) {
    return []
  }
  return names.map((name) => (typeof name === 'string' ? name : ''))
}

/**
 * The labels this rubric's own entries are named for, in report order: the
 * `{prefix}{label}{suffix}` names, e.g. `Outcome p(Resolved)`. The group
 * holds only this evaluator's entries, so nothing else can match.
 */
function namedLabels(
  entries: Map<string, Entry>,
  prefix: string,
  suffix = ''
): string[] {
  return [...entries.keys()].flatMap((name) =>
    name.startsWith(prefix) &&
    name.endsWith(suffix) &&
    name.length > prefix.length + suffix.length
      ? [name.slice(prefix.length, name.length - suffix.length)]
      : []
  )
}

/** The probability in relay's multi-label reason, "p = 0.97 (applies at 0.45)". */
function reasonProbability(reason: string | null): number | null {
  const match = reason?.match(/^p = ([\d.eE-]+)/)
  return match ? Number(match[1]) : null
}

const num = (value: unknown) => (typeof value === 'number' ? value : null)

/** One evaluator's own entries, looked up by the names relay gives them. */
type Group = {
  key: string
  title: string
  args: Record<string, unknown> | null
  assertions: Map<string, Entry>
  scores: Map<string, Entry>
  labels: Map<string, Entry>
}

function buildRubric({
  key,
  title,
  args,
  assertions,
  scores,
  labels,
}: Group): RubricResult {
  const type =
    typeof args?.rubric_type === 'string' ? args.rubric_type : 'pass_fail'
  const agentPrompt = agentPromptUse(args)
  const careforceOnly = args?.careforce_only === true
  const score = (name: string) => num(scores.get(name)?.value)
  const probabilities = (names: string[]): Probability[] =>
    names.flatMap((label) => {
      const probability = score(`${title} p(${label})`)
      return probability === null ? [] : [{ label, probability }]
    })

  if (type === 'classification') {
    const pick = labels.get(title)
    return {
      key,
      title,
      agentPrompt,
      careforceOnly,
      type,
      reason: pick?.reason ?? null,
      choice: typeof pick?.value === 'string' ? pick.value : null,
      probabilities: probabilities(namedLabels(scores, `${title} p(`, ')')),
    }
  }
  if (type === 'multi_label') {
    return {
      key,
      title,
      agentPrompt,
      careforceOnly,
      type,
      reason: null,
      labels: namedLabels(assertions, `${title}: `).flatMap((label) => {
        const verdict = assertions.get(`${title}: ${label}`)
        if (!verdict) {
          return []
        }
        return [
          {
            label,
            applies: verdict.value === true,
            probability:
              reasonProbability(verdict.reason) ??
              score(`${title} p(${label})`),
            reason: verdict.reason,
          },
        ]
      }),
    }
  }
  if (type === 'rating') {
    const options = levelNames(args)
    const nearest = labels.get(`${title} level`)
    return {
      key,
      title,
      agentPrompt,
      careforceOnly,
      type,
      reason: nearest?.reason ?? null,
      score: score(title),
      levels: options.length,
      levelName: typeof nearest?.value === 'string' ? nearest.value : null,
      // Each level keeps its own index, so a level missing from the report
      // leaves a gap rather than shifting later levels onto earlier names.
      probabilities: options.flatMap((name, i) => {
        const probability = score(`${title} p(${i + 1})`)
        return probability === null
          ? []
          : [{ label: ratingLevelName(i, name), probability }]
      }),
    }
  }
  if (type !== 'pass_fail') {
    return {
      key,
      title,
      agentPrompt,
      careforceOnly,
      type: 'unsupported',
      rubricType: type,
      reason: null,
    }
  }
  const verdict = assertions.get(title)
  return {
    key,
    title,
    agentPrompt,
    careforceOnly,
    type: 'pass_fail',
    reason: verdict?.reason ?? null,
    passed: verdict?.value === true,
    probability: score(`${title} p(pass)`),
  }
}

/**
 * Splits a case's entries into one group per evaluator, so a rubric only ever
 * reads its own results. Grouping by title alone would merge two rubrics that
 * share a title (hiding one's verdict), and looking names up across the whole
 * report would let rubric "Empathy level" stand in for rating "Empathy"'s
 * `Empathy level` label.
 *
 * An evaluator is identified by its arguments (title, criteria, type,
 * options). Two evaluators with identical arguments write the same names, and
 * pydantic-evals suffixes the clashing dict keys (`Empathy_2`), so the nth
 * entry of a name within one identity belongs to the nth copy. An entry with
 * no title argument (the old LLM judge) is its own pass/fail rubric.
 */
function groupByEvaluator(kinds: {
  assertions: Entry[]
  scores: Entry[]
  labels: Entry[]
}): Group[] {
  const groups = new Map<string, Group>()
  const seen = new Map<string, number>()
  for (const kind of ['assertions', 'labels', 'scores'] as const) {
    for (const entry of kinds[kind]) {
      const argTitle =
        typeof entry.args?.title === 'string' ? entry.args.title : null
      let groupKey: string
      if (argTitle === null) {
        if (kind !== 'assertions') {
          continue
        }
        groupKey = `untitled:${entry.key}`
      } else {
        const identity = JSON.stringify(entry.args)
        const seenKey = `${kind}\u0000${identity}\u0000${entry.name}`
        const copy = seen.get(seenKey) ?? 0
        seen.set(seenKey, copy + 1)
        groupKey = `${identity}#${copy}`
      }
      let group = groups.get(groupKey)
      if (!group) {
        group = {
          key: groupKey,
          title: argTitle ?? entry.name,
          args: argTitle === null ? null : entry.args,
          assertions: new Map(),
          scores: new Map(),
          labels: new Map(),
        }
        groups.set(groupKey, group)
      }
      group[kind].set(entry.name, entry)
    }
  }
  return [...groups.values()]
}

function ungradedRubrics(raw: unknown): UngradedRubric[] {
  if (!Array.isArray(raw)) {
    return []
  }
  return raw.flatMap((failure, index) => {
    if (!isRecord(failure)) {
      return []
    }
    const source = isRecord(failure.source) ? failure.source : null
    const args = source && isRecord(source.arguments) ? source.arguments : null
    const title =
      typeof args?.title === 'string'
        ? args.title
        : typeof failure.name === 'string'
          ? failure.name
          : 'Rubric'
    return [
      {
        key: `ungraded:${index}`,
        title,
        rubricType:
          typeof args?.rubric_type === 'string'
            ? args.rubric_type
            : 'pass_fail',
        error:
          typeof failure.error_message === 'string'
            ? failure.error_message
            : null,
        careforceOnly: args?.careforce_only === true,
      },
    ]
  })
}

export function parseRubricReport(report: unknown): ParsedRubricReport | null {
  if (!isRecord(report)) {
    return null
  }
  const cases = report.cases
  if (!Array.isArray(cases) || cases.length === 0) {
    return null
  }
  const first = cases[0]
  if (!isRecord(first)) {
    return null
  }
  const groups = groupByEvaluator({
    assertions: entries(first.assertions),
    scores: entries(first.scores),
    labels: entries(first.labels),
  })
  return {
    rubrics: groups.map(buildRubric),
    ungraded: ungradedRubrics(first.evaluator_failures),
  }
}

/**
 * Why relay's judge gate left a call ungraded: it had no conversation to judge.
 * Written in place of a pydantic-evals report, as
 * `{"not_graded": {"reason": ..., "label": ...}}` (`cf_call_eval.py`).
 */
export type NotGradedReason =
  | 'no_conversation_ended_reason'
  | 'no_conversation_label'
  | 'no_human_turn'

export type NotGraded = {
  /** A reason newer than this UI still reads as not graded. */
  reason: NotGradedReason | string
  /** The call's post-call label when the judge skipped it. */
  label: string | null
}

export function parseNotGraded(report: unknown): NotGraded | null {
  if (!isRecord(report) || !isRecord(report.not_graded)) {
    return null
  }
  const { reason, label } = report.not_graded
  return {
    reason: typeof reason === 'string' ? reason : '',
    label: typeof label === 'string' && label.trim() ? label : null,
  }
}

/** One line on why a call wasn't graded, for the call's panel. */
export function notGradedReasonText({ reason, label }: NotGraded): string {
  switch (reason) {
    case 'no_conversation_ended_reason':
      return 'The call went to voicemail, wasn’t answered, or was busy.'
    case 'no_conversation_label':
      return label
        ? `The call was labeled “${label}”, so no one was reached.`
        : 'The call’s label says no one was reached.'
    case 'no_human_turn':
      return 'Nobody on the other end said anything.'
    default:
      return 'The call had no conversation to grade.'
  }
}

/** Short names for each reason, for counts on the Metrics page. */
export const NOT_GRADED_REASON_LABEL: Record<NotGradedReason, string> = {
  no_conversation_ended_reason: 'Voicemail, no answer or busy',
  no_conversation_label: 'Labeled as no conversation',
  no_human_turn: 'Nobody spoke',
}

export type RubricReport = {
  /** Parsed verdicts, or null when the stored JSON isn't a shape we parse. */
  parsedReport: ParsedRubricReport | null
  /** Set when the judge skipped the call for having no conversation. */
  notGraded: NotGraded | null
  /** Pretty-printed stored JSON, for Copy and the raw fallback. */
  reportJsonText: string
  hasReport: boolean
}

/**
 * Everything the Rubric Analysis panel shows for one call, derived from its
 * saved `app_calls.eval_report_json`. Reports are written by the post-call
 * decision model in relay; nothing here runs one.
 */
export function rubricReport(evalReportJson: unknown): RubricReport {
  if (!hasNonEmptyEvalReportJson(evalReportJson)) {
    return {
      parsedReport: null,
      notGraded: null,
      reportJsonText: '',
      hasReport: false,
    }
  }
  let reportJsonText: string
  try {
    reportJsonText = JSON.stringify(evalReportJson, null, 2)
  } catch {
    reportJsonText = String(evalReportJson)
  }
  return {
    parsedReport: parseRubricReport(evalReportJson),
    notGraded: parseNotGraded(evalReportJson),
    reportJsonText,
    hasReport: true,
  }
}
